import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  CircularProgress,
  Alert,
  TextField,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import { getMyBranchesRequestsWithItems } from './stock-transfer';

interface RequisitionItem {
  bom_id: string;
  product_name: string;
  quantity: number;
  price: number;
  total: number;
  status: string;
  created_at: string;
}

const BranchRequestPage: React.FC = () => {
  const [requests, setRequests] = useState<RequisitionItem[]>([]);
  const [filtered, setFiltered] = useState<RequisitionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [selected, setSelected] = useState<RequisitionItem | null>(null);

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        const res = await getMyBranchesRequestsWithItems();
        if (res.data?.status === 'success') {
          setRequests(res.data.data || []);
        } else {
          setError(res.data?.message || 'Failed to load requests');
        }
      } catch (err) {
        console.error('Error fetching requisitions:', err);
        setError('An error occurred while fetching requisitions.');
      } finally {
        setLoading(false);
      }
    };

    fetchRequests();
  }, []);

  useEffect(() => {
    const term = search.toLowerCase();
    const filteredData = requests.filter((r) => {
      const matchSearch = r.product_name.toLowerCase().includes(term);
      const matchStatus = statusFilter ? r.status === statusFilter : true;
      const matchStart = startDate ? new Date(r.created_at) >= new Date(startDate) : true;
      const matchEnd = endDate ? new Date(r.created_at) <= new Date(endDate) : true;
      return matchSearch && matchStatus && matchStart && matchEnd;
    });
    setFiltered(filteredData);
  }, [search, statusFilter, startDate, endDate, requests]);

  const handleChangePage = (_: any, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={4}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h5" gutterBottom>
        Branch Requisition Requests
      </Typography>

      <Box display="flex" gap={2} mb={2} flexWrap="wrap">
        <TextField
          label="Search by Product"
          variant="outlined"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="Start Date"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />

        <TextField
          label="End Date"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />
      </Box>

      <Paper>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>BOM ID</TableCell>
              <TableCell>Product</TableCell>
              <TableCell>Quantity</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((req) => (
              <TableRow key={req.bom_id}>
                <TableCell>{req.bom_id}</TableCell>
                <TableCell>{req.product_name}</TableCell>
                <TableCell>{req.quantity}</TableCell>
                <TableCell>₦{req.price.toLocaleString()}</TableCell>
                <TableCell>₦{req.total.toLocaleString()}</TableCell>
                <TableCell>{req.status}</TableCell>
                <TableCell>{new Date(req.created_at).toLocaleDateString()}</TableCell>
                <TableCell>
                  <Button size="small" onClick={() => setSelected(req)}>
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        <DialogTitle>BOM Detail</DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Box>
              <Typography><strong>BOM ID:</strong> {selected.bom_id}</Typography>
              <Typography><strong>Product:</strong> {selected.product_name}</Typography>
              <Typography><strong>Quantity:</strong> {selected.quantity}</Typography>
              <Typography><strong>Price:</strong> ₦{selected.price.toLocaleString()}</Typography>
              <Typography><strong>Total:</strong> ₦{selected.total.toLocaleString()}</Typography>
              <Typography><strong>Status:</strong> {selected.status}</Typography>
              <Typography><strong>Date:</strong> {new Date(selected.created_at).toLocaleString()}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BranchRequestPage;